import { Button, Label, Spinner, Textarea, TextInput } from 'flowbite-react';
import React, { FormEvent, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { sendOnlineTestToJobseeker } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { SendOnlineTestToUserInput } from 'types';
import { useUserContext } from 'src/context/UserProvider';
import { Dropdown } from 'shared-utils';
import Select, { SingleValue } from 'react-select';
import { useGetAllPrepCoaches } from 'src/hooks/user.hooks';

const RecruiterCandidateSendOnlineTest = ({
    userId,
    onSubmit,
    onFinish,
    userName,
    isLoading: outSideApiLoading = false,
    positionId,
}: {
    userId?: string;
    userName?: string;
    positionId?: string;
    isLoading?: boolean;
    onSubmit?: (data: SendOnlineTestToUserInput) => void;
    onFinish?: () => void;
}) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { isAccountManagerLoggedIn } = useUserContext();

    const [isLoading, setIsLoading] = React.useState(outSideApiLoading);
    const [onlineTestFormData, setOnlineTestFormData] = React.useState<SendOnlineTestToUserInput>({
        positionId: '',
        userId: '',
        prepCoach: '',
        onlineTestLink: '',
        remarks: '',
    });

    const sendOnlineTestToJobseekerFn = useMutation(['sendOnlineTestToJobseeker'], sendOnlineTestToJobseeker, {
        onSuccess(data) {
            queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
            toast({
                title: `Online test sent to ${userName}`,
                type: 'success',
            });
            setOnlineTestFormData({
                positionId: '',
                userId: '',
                prepCoach: '',
                onlineTestLink: '',
                remarks: '',
            });
            onFinish && onFinish();
        },
        onError(error: Error) {
            toast({
                title: `Error occured ${error.message}`,
                type: 'error',
            });
        },
    });

    const { data: prepCoachesData, isLoading: prepCoachesLoading } = useGetAllPrepCoaches();

    // Validate URL format
    const isValidUrl = (url: string): boolean => {
        if (!url || !url.trim()) {
            return false;
        }
        const trimmed = url.trim();
        try {
            // Try to create a URL object - this validates the format
            const urlObj = new URL(trimmed);
            // Check if it has a valid protocol (http or https)
            if (!['http:', 'https:'].includes(urlObj.protocol)) {
                return false;
            }
            // Check if it has a valid hostname (domain)
            if (!urlObj.hostname || urlObj.hostname.length < 3) {
                return false;
            }
            // Check if hostname contains at least one dot (for domain like example.com)
            // This prevents URLs like "https://test" or "https://invalid"
            if (!urlObj.hostname.includes('.')) {
                return false;
            }
            // Check if hostname doesn't contain spaces (which would be URL encoded)
            if (urlObj.hostname.includes(' ')) {
                return false;
            }
            return true;
        } catch (error) {
            // If URL constructor throws, try adding https:// prefix
            try {
                const urlWithProtocol = trimmed.startsWith('http://') || trimmed.startsWith('https://')
                    ? trimmed
                    : `https://${trimmed}`;
                const urlObj = new URL(urlWithProtocol);
                if (!['http:', 'https:'].includes(urlObj.protocol)) {
                    return false;
                }
                if (!urlObj.hostname || urlObj.hostname.length < 3 || !urlObj.hostname.includes('.')) {
                    return false;
                }
                if (urlObj.hostname.includes(' ')) {
                    return false;
                }
                return true;
            } catch (e) {
                return false;
            }
        }
    };

    const formatOnlineTestLink = (link?: string) => {
        if (!link) {
            return '';
        }
        const trimmed = link.trim();
        if (!trimmed) {
            return '';
        }
        // Validate first
        if (!isValidUrl(trimmed)) {
            return '';
        }
        const normalizedLink = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        try {
            const parsedUrl = new URL(normalizedLink);
            return parsedUrl.toString();
        } catch (error) {
            return '';
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const sanitizedLink = formatOnlineTestLink(onlineTestFormData.onlineTestLink);

        if (!sanitizedLink) {
            toast({
                title: 'Please enter a valid online test link',
                type: 'error',
            });
            return;
        }

        if (onSubmit) {
            onSubmit({
                ...onlineTestFormData,
                positionId: positionId ?? '',
                userId: userId ?? '',
                onlineTestLink: sanitizedLink,
                remarks: onlineTestFormData.remarks?.trim(),
            });
        } else {
            sendOnlineTestToJobseekerFn.mutate({
                ...onlineTestFormData,
                positionId: positionId ?? '',
                userId: userId ?? '',
                onlineTestLink: sanitizedLink,
                remarks: onlineTestFormData.remarks?.trim(),
            });
        }
    };

    useEffect(() => {
        setOnlineTestFormData({
            positionId: '',
            userId: '',
            prepCoach: '',
            onlineTestLink: '',
            remarks: '',
        });
    }, []);

    const prepCoachesOptions =
        prepCoachesData?.users?.map(u => ({ label: `${u.name} <${u.email}>`, value: String(u._id) })) ?? [];

    return (
        <form onSubmit={handleSubmit}>
            <div>
                <div className="mb-[8px] flex justify-between items-end">
                    <div>
                        <Label>{'Online test link '}</Label>
                        <span className="text-[red]">*</span>
                    </div>
                </div>

                <TextInput
                    type="url"
                    name="onlineTestLink"
                    value={onlineTestFormData.onlineTestLink || ''}
                    placeholder="https://example.com/online-test"
                    helperText="Enter a valid URL starting with http:// or https://"
                    onChange={e => {
                        setOnlineTestFormData(prev => {
                            return { ...prev, onlineTestLink: e.target.value };
                        });
                    }}
                />
            </div>

            <div className="mt-[16px]">
                <div className="mb-[8px] flex justify-between items-end">
                    <div>
                        <Label>{'Remarks'}</Label>
                    </div>
                    <span className="italic text-xs text-[#6B7280] ">
                        {(onlineTestFormData?.remarks?.length || 0) + ' / ' + 640}
                    </span>
                </div>
                <Textarea
                    name="remarks"
                    maxLength={640}
                    value={onlineTestFormData.remarks || ''}
                    placeholder="Add any instructions or remarks for the candidate..."
                    className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[120px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
                    onChange={e => {
                        setOnlineTestFormData(prev => {
                            return { ...prev, remarks: e.target.value };
                        });
                    }}
                ></Textarea>
            </div>

            {/* Prep Coach */}
            {isAccountManagerLoggedIn ? (
                <div className="mt-[16px] whiteBgSelect">
                    <div className="mb-[8px]">
                        <Label>{'Prep Coach'}</Label>
                        <span className="text-[red]">*</span>{' '}
                    </div>
                    <Select
                        classNamePrefix="my-select-plans"
                        className="single-value-select-tag"
                        isLoading={prepCoachesLoading}
                        options={prepCoachesOptions}
                        closeMenuOnSelect
                        value={prepCoachesOptions.find(op => op.value === onlineTestFormData.prepCoach)}
                        onChange={(val: SingleValue<Dropdown>) => {
                            if (val) setOnlineTestFormData(prev => ({ ...prev, prepCoach: String(val.value) }));
                        }}
                    />
                </div>
            ) : null}

            <Button
                type="submit"
                className="w-full min-h-[42px] mt-[20px] bg-[#7777E7]"
                color="purplePlaced"
                disabled={
                    sendOnlineTestToJobseekerFn.isLoading ||
                    !onlineTestFormData.onlineTestLink ||
                    (isAccountManagerLoggedIn && !onlineTestFormData.prepCoach) ||
                    outSideApiLoading
                }
            >
                {sendOnlineTestToJobseekerFn.isLoading || outSideApiLoading ? (
                    <div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
                        <Spinner />
                    </div>
                ) : (
                    'Send Online Test'
                )}
            </Button>
        </form>
    );
};

export default RecruiterCandidateSendOnlineTest;
